import DriveView from "../DriveView";
import * as logger from "../utils/logger";
import { customPageControlFunc } from "../utils/pageLoader";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import toastr from "../utils/toastr";
import { BigSpinner } from "../utils/spinner";
import { IAddinApi } from "../services/addin";
import { hide } from "../utils/vanilla";
import { MobileActions } from "../store/mobile/actions";
import {
    IAddinCacheItem, getFullHash, addinsCache, getExtendedAppState, getAddinConfig, AddinCacheSource,
    getAddinStorageKey, downloadAddin,
    IAddinInformation
} from "../utils/addinHelper";
import { PageLevel } from "../store/mobile/types";
import { activateAddinPage, deactivateAddinPage } from "../utils/native/addinBottomBar";
import AddinTranslationService from "../services/addinTranslationService";
import addinTmpl from "./addin.tmpl";
export default class AddinView extends DriveView<Backbone.Model> {

    initialized = false;
    spinner;
    name: string;
    api: IAddinApi;
    apiUser;
    appState;
    promise: Promise;
    addin: IAddinCacheItem;
    addinTitle: string | undefined;

    constructor(options) {
        super(options, {
            title: "",
            activeSection: "",
            pageLevel: PageLevel.SUBMENU_WITH_BACK_BUTTON
        });
        this.name = atob(options.state.addinName);

        this.api = this.app.addinService.getAddinApi(this.name);
        this.apiUser = this.api && this.api.getUser();
        this.appState = getExtendedAppState(this.app.store.getState().mobile, this.app.router);

        // Translate method for addin
        this.addinTitle = getAddinConfig(this.name, this.app.databaseCache, this.app.userService.getCurrentUser())?.menuName;
        this.appState.translate = (arg: HTMLElement | string): void | string => {
            let translator = new AddinTranslationService(getAddinStorageKey(this.name, this.app.databaseCache, this.app.userService.getCurrentUser()));

            if (typeof arg === "string") {
                return translator.translateStr(arg);
            }

            translator.translate(arg);
        };

        this.addin = addinsCache.get(this.name);
    }

    render() {
        // If previously called, cancel it
        this.promise && this.promise.cancel();

        this.promise = Promise.resolve()
            .then(() => this.startSpinning())
            .then(() => {
                // Refresh from cache — the addin may have been added after this view was constructed
                // (e.g. Phase 1 preload or Phase 2 startup completed after construction).
                // This prevents re-injecting scripts that executeStartupForCachedAddin already injected.
                this.addin = this.addin || addinsCache.get(this.name);

                // If addin is already cached, return cached content to avoid re-injecting scripts
                if (this.addin) {
                    logger.log(logger.Source.ADDIN, `Using cached addin content for "${ this.name }", skipping script injection`);
                    return { metadata: this.addin.metadata, addinContent: this.addin.addinContent };
                }
                // Not cached, download it
                logger.log(logger.Source.ADDIN, `Addin "${ this.name }" not in cache, downloading and injecting scripts`);
                return downloadAddin(null, this.app.databaseCache, this.name, this.api, this.app.userService.getCurrentUser());
            })
            .then(addinContent => this.stopSpinning(addinContent))
            .then(addinInformation => this.init(addinInformation))
            .then(() => this.focus())
            .then(() => {
                this.app.store.dispatch(MobileActions.setPageState({
                    title: this.addinTitle || "Add-In",
                    activeSection: this.name,
                    pageLevel: PageLevel.SUBMENU_WITH_BACK_BUTTON
                }));
            })
            .catch((err) => {
                void this.translator.translate("The Add-in cannot be installed. Please contact your Administrator.").then(toastr.error);

                // don't log unknown addin errors
                const errorMessage = (err || {}).message;
                if (errorMessage && errorMessage.indexOf("Unknown addin's error") === -1) {
                    let message = `Add-in "${ this.name }" cannot be loaded. Please contact your administrator. Message: ${ errorMessage }`;
                    logger.error(logger.Source.ADDIN, message, this.apiUser ? this.apiUser.name : null);
                }
            });

        return this;
    }

    focus() {
        // Before the add-in's own focus() runs, so a bottom bar request it makes there is accepted (MYG-154380).
        activateAddinPage(this, this.name);

        // On first init, the dom is a string and hasn't been added as DOM elements
        // 2nd render will have the elements that were created from the innerHTML set
        (this.addin.dom as HTMLElement[] || []).forEach(child => {
            this.el.appendChild(child);
        });

        this.addin.func.focus(this.api, this.appState);
    }

    blur() {
        if (this.addin && typeof (this.addin.func.blur)  === "function") {
            this.addin.func.blur(this.api, this.appState);
        }
        this.updateAddinCacheDOM();
    }

    updateAddinCacheDOM() {
        addinsCache.updateDOM(this.name, this.el ? Array.prototype.slice.call(this.el.childNodes) as HTMLElement[] : []);
    }

    remove() {
        // A render still pending would call focus() on a removed view and take the bottom bar from the next add-in.
        this.promise && this.promise.cancel();
        try {
            this.blur();
        } finally {
            deactivateAddinPage(this);
        }
        super.remove();
        return this;
    }

    private init({ addinContent, metadata }: IAddinInformation): Promise<void> {
        if (this.addin && this.addin.isStartedOnAddinPage) {
            return Promise.resolve();
        }

        let addinFunction = {
            initialize: (..._) => {},
            focus: (..._) => {},
            blur: (..._) => {}
        };

        // Make dom available before before addinDefn or initialize execution
        this.el.innerHTML = addinContent.bodyContent;

        return new Promise((resolve, reject) => {
            try {
                // Always create a fresh function instance if not cached or if cached function is null
                // This ensures DOM elements are captured correctly
                if (this.addin && this.addin.func) {
                    addinFunction = this.addin.func;
                } else {
                    let addinDefinition = customPageControlFunc(getFullHash(this.name));
                    addinFunction = addinDefinition(this.api, this.appState, metadata);
                }

                addinFunction.initialize(this.api, this.appState, () => {
                    if (!this.addin) {
                        addinsCache.add(
                            this.name,
                            {
                                func: addinFunction,
                                dom: null,
                                addinContent,
                                metadata,
                                isStartedOnDashboard: false,
                                isStartedOnAddinPage: true
                            },
                            AddinCacheSource.INIT
                        );
                    } else {
                        this.addin.isStartedOnAddinPage = true;
                    }

                    this.addin = addinsCache.get(this.name);
                    this.updateAddinCacheDOM();
                    logger.log(logger.Source.ADDIN, `Loaded Add-In "${ this.name }"`, this.apiUser ? this.apiUser.name : null);
                    resolve();
                });
            } catch (e) {
                // don't log unknown addin errors
                if (e.message && e.message.indexOf("Unknown addin's error") === -1) {
                    logger.error(logger.Source.ADDIN, `Error initializing add-in: ${ e.message }`);
                }
                reject(new Error(`Error initializing add-in: ${ e.message }`));
            }
        });
    }

    private startSpinning () {
        if (this.addin) {
            return;
        }

        this.el.innerHTML = addinTmpl;

        let spinnerElement = document.getElementById("addin-spinner");
        this.spinner = new BigSpinner({ zIndex: 2e9 });
        spinnerElement && this.spinner.spin(spinnerElement);
    }

    private stopSpinning<T>(addinContent: T): T {
        if (!this.addin) {
            this.spinner && this.spinner.stop();

            let addinSpinnerElement = document.getElementById("addin-spinner");
            addinSpinnerElement && hide(addinSpinnerElement);
        }

        return addinContent;
    }
}
